'use client';
import SafeImage from '@ui/SafeImage';
import { formatCurrency } from '@util/index';
import { ChevronDownIcon, ChevronUpIcon } from 'lucide-react';
import { useState } from 'react';
import sanitize from 'sanitize-html';
import Link from 'next/link';
import { slugify } from '@util/urlHelpers';
import Button from '@ui/Button';
import { useAuth } from 'context/AuthContext';
import { DataDepot } from 'app/data-depot/_components/DataDepotForm';
import BikeFitment from '@c/product-details/BikeFitment';

export default function MasterProductHeader({
  product,
  fitment,
  mpn,
}: {
  product: DataDepot;
  fitment: Map<string, Map<string, { years: number[]; rideType: string }>>;
  mpn: string;
}) {
  const { userDoc } = useAuth();
  const [showSKUTable, setShowSKUTable] = useState(false);

  const variation = product.variations?.find((v) => v.sku === mpn);
  const parsedTitle = variation?.color
    ? `${product.title} - ${variation.color}`
    : product.title;

  const sections = [
    {
      label: 'Retail Price',
      value: variation?.price ?? product.price,
    },
    {
      label: 'Style',
      value: variation?.color || variation?.size,
    },
    {
      label: 'Category',
      value: product.categories[product.categories.length - 1],
    },
    {
      label: 'Brand',
      value: product.brand,
    },
    {
      label: 'MPN / OEM Part Number',
      value: mpn,
    },
  ];
  const [showMore, setShowMore] = useState(false);

  return (
    <div className="flex w-full flex-col items-center gap-x-16 gap-y-16 px-8 py-16 sm:flex-row sm:items-start sm:gap-y-0 sm:px-4">
      <SafeImage
        alt={`${product.title} ${variation?.size}`}
        src={variation?.image ?? product.thumbnail}
        className="h-[30rem] w-[30rem] object-contain"
      />

      <div className="flex flex-col gap-y-4">
        <h1 className="flex gap-4 text-[3rem] font-semibold text-black sm:text-[3.8rem]">
          {parsedTitle}
          {userDoc?.roles?.admin && (
            <Button text="Edit" href={`/data-depot/${product.id}`} />
          )}
        </h1>

        <div className="flex flex-col gap-y-8">
          <div className="flex flex-col gap-y-2">
            <span className="text-[1.8rem] font-medium text-zinc-500 sm:text-[1.5rem]">
              Product Description
            </span>

            {product.description_html ? (
              <div>
                <div
                  className={`${
                    showMore ? '' : 'line-clamp-4 max-h-[30rem]'
                  } overflow-x-auto text-[1.8rem] font-medium display-webkit-box webkit-line-clamp-4 -webkit-box-orient-vertical sm:max-w-[70rem] sm:text-[1.6rem]`}
                  dangerouslySetInnerHTML={{
                    __html: sanitize(product.description_html, {
                      allowedTags: sanitize.defaults.allowedTags.filter(
                        (tag) => tag !== 'a'
                      ),
                    }),
                  }}
                />
                <button
                  type="button"
                  onClick={() => setShowMore(!showMore)}
                  className="text-[1.8rem] font-medium text-brand-secondary sm:text-[1.6rem]"
                >
                  {showMore ? 'Show Less' : 'Show More'}
                </button>
              </div>
            ) : (
              <div>
                <p
                  className={`${
                    showMore ? '' : 'line-clamp-4'
                  } text-[1.8rem] font-medium text-zinc-700 sm:text-[1.6rem]`}
                >
                  {product.description}
                </p>
                <button
                  type="button"
                  onClick={() => setShowMore(!showMore)}
                  className="text-[1.8rem] font-medium text-brand-secondary sm:text-[1.6rem]"
                >
                  {showMore ? 'Show Less' : 'Show More'}
                </button>
              </div>
            )}
          </div>

          <div className="grid grid-cols-2 items-start gap-x-[12rem] gap-y-8 sm:flex sm:flex-wrap">
            {sections
              .filter((section) => section.value)
              .map((section) => {
                const redText = ['Brand', 'Category', 'SKU'].includes(
                  section.label
                );

                const isPrice = section.label === 'Retail Price';
                const isSKU = section.label === 'MPN / OEM Part Number';

                if (isSKU) {
                  return (
                    <div
                      key={section.label}
                      className="flex basis-[20rem] flex-col gap-2"
                    >
                      <span className="text-[1.8rem] font-medium text-zinc-500 sm:text-[1.5rem]">
                        {section.label}
                      </span>

                      {product.has_variations ? (
                        <button
                          type="button"
                          onClick={() => setShowSKUTable(!showSKUTable)}
                          className={`flex items-center gap-x-2 text-[2rem] font-semibold text-brand-secondary sm:text-[1.6rem]`}
                        >
                          {showSKUTable ? 'Hide' : 'Show'} SKU info{' '}
                          {showSKUTable ? (
                            <ChevronUpIcon className="h-10 w-10 text-brand-secondary sm:h-8 sm:w-8" />
                          ) : (
                            <ChevronDownIcon className="h-10 w-10 text-brand-secondary sm:h-8 sm:w-8" />
                          )}
                        </button>
                      ) : (
                        <span className="text-[2rem] font-semibold text-zinc-700 sm:text-[1.6rem]">
                          {section.value}
                        </span>
                      )}
                    </div>
                  );
                }

                return (
                  <div key={section.label} className="flex flex-col gap-2">
                    <span className="text-[1.8rem] font-medium text-zinc-500 sm:text-[1.5rem]">
                      {section.label}
                    </span>
                    <span
                      className={`text-[2rem] sm:text-[1.6rem] ${
                        redText
                          ? 'font-semibold text-brand-secondary'
                          : 'font-medium text-zinc-700'
                      }`}
                    >
                      {isPrice
                        ? formatCurrency(Number(section.value ?? 0))
                        : section.value}
                    </span>
                  </div>
                );
              })}
          </div>
        </div>

        <div
          className={`mt-8 overflow-hidden rounded-[1rem] ${
            showSKUTable ? 'max-h-[40rem] border border-zinc-200' : 'max-h-0'
          } transition-max-height duration-200 ease-in-out`}
        >
          <div className="grid w-full">
            <div className="sticky top-0 grid grid-cols-2 border-b border-zinc-200 bg-[#F1F1F1]">
              <div className="col-span-2 p-4 text-left text-[1.5rem] font-medium text-[#444444]">
                <div className="grid grid-cols-2">
                  <div>Size / Style</div>
                  <div>SKU Number</div>
                </div>
              </div>
            </div>
            <div className="max-h-[40rem] overflow-y-auto">
              {product.variations
                ?.sort((a, b) => {
                  // if matches the color bring to the top
                  if (a.color === variation?.color) return -1;
                  if (b.color === variation?.color) return 1;
                  return 0;
                })
                .map((variation) => {
                  const isLastItem =
                    product.variations?.indexOf(variation) ===
                    (product.variations?.length ?? 0) - 1;

                  return (
                    <div
                      key={variation.sku}
                      className={`grid grid-cols-2 ${
                        variation.sku === mpn
                          ? 'bg-brand-secondary text-white'
                          : 'text-[#444444]'
                      }`}
                    >
                      <div
                        className={`p-4 text-[1.6rem] ${
                          isLastItem ? '' : 'border-b border-zinc-200'
                        }`}
                      >
                        {variation.size !== 'N/A' && variation.size}
                        {variation.color &&
                          `${variation.size !== 'N/A' ? ' / ' : ''}${
                            variation.color
                          }`}
                      </div>
                      <div
                        className={`border-l p-4 text-[1.6rem] ${
                          isLastItem ? '' : 'border-b border-zinc-200'
                        }`}
                      >
                        <Link
                          className="underline"
                          href={`/product/${slugify(product.title)}/${
                            variation.sku
                          }`}
                        >
                          {variation.sku}
                        </Link>
                      </div>
                    </div>
                  );
                })}
            </div>
          </div>
        </div>

        {!!fitment.size && (
          <div>
            <h3 className="mb-4 text-[2.125rem] font-semibold">Part Fitment</h3>
            <BikeFitment product={product} fitment={fitment} />
          </div>
        )}

        <div className="mt-8 flex flex-col items-center gap-4 rounded-[1rem] border border-zinc-200 bg-zinc-50 p-8">
          <h3 className="text-[2rem] font-semibold text-zinc-800">
            Not the right product?
          </h3>
          <p className="text-center text-[1.6rem] text-zinc-600">
            Search through all our listings to find exactly what you need
          </p>
          <Link
            href={`/search?keyword=${mpn}&skip_mpn=true`}
            className="inline-flex items-center justify-center rounded-[0.8rem] bg-brand-secondary px-8 py-4 text-[1.6rem] font-semibold text-white transition-colors hover:bg-brand-secondary/90"
          >
            Search All Listings for {mpn}
          </Link>
        </div>
      </div>
    </div>
  );
}
